import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { NOTIFICATION_TYPE } from '../../../constants/common'
import { QA_NAME_CONFIRM_BTN } from '../../../constants/qa'
import { SIZE } from '../../../constants/size'
import Button from '../../Button'
import Modal, { Box, Buttons, Content, Header } from '../../Modal'
import { NotificationInline } from '../../Notification'
import { Skeleton } from '../../Skeleton'
import Text from '../../Text'
import { UserMultiValue } from '../../User'
import { getDocFlowEpostValidationPreview } from '../DocFlow.api'
import { useDocFlowContext } from '../DocFlow.hooks'
import { toRecipientArchivedMultiValue } from '../DocFlow.utils.recipients'

import { DocFlowEpostValidationPreviewImage } from './DocFlowEpostValidationPreviewImage'
import {
  DOC_FLOW_EPOST_VALIDATION_PREVIEW_TITLE_KEY,
  DocFlowEpostValidationPreviewErrorFieldName,
  DocFlowEpostValidationPreviewFieldName,
  QA_NAME_DOC_FLOW_EPOST_VALIDATION_PREVIEW_MODAL,
} from './DocFlowEpostValidationPreviewModal.constants'
import { getDocFlowEpostValidationPreviewFileName } from './DocFlowEpostValidationPreviewModal.utils'

import * as styles from './DocFlowEpostValidationPreviewModal.module.css'

function DocFlowEpostValidationPreviewModal({ recipient, sendingId, onClose }) {
  const { t } = useTranslation()

  const { docFlow, documentId } = useDocFlowContext()

  const [preview, setPreview] = useState(null)
  const [isLoading, setLoading] = useState(true)
  const [isUnavailable, setUnavailable] = useState(false)

  useEffect(() => {
    getDocFlowEpostValidationPreview({ documentId, sendingId })
      .then(({ data }) => {
        setPreview(data)
      })
      .catch(() => {
        setUnavailable(true)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [documentId, sendingId])

  const errors = preview?.[DocFlowEpostValidationPreviewFieldName.Errors]
  const previewImage =
    preview?.[DocFlowEpostValidationPreviewFieldName.PreviewImage]
  const previewFileName = getDocFlowEpostValidationPreviewFileName({
    docFlowName: docFlow?.name,
    recipient,
  })

  return (
    <Modal
      qaName={QA_NAME_DOC_FLOW_EPOST_VALIDATION_PREVIEW_MODAL}
      onClose={onClose}
    >
      <Box size={SIZE.LARGE}>
        <Header
          text={t(DOC_FLOW_EPOST_VALIDATION_PREVIEW_TITLE_KEY)}
          onClose={onClose}
        />

        <Content
          contentWrapperClassName={styles.contentWrapper}
          size={SIZE.LARGE}
        >
          <UserMultiValue {...toRecipientArchivedMultiValue(recipient)} />

          {isLoading ? <Skeleton className={styles.skeleton} /> : null}

          {isUnavailable ? (
            <NotificationInline
              text={t('Files.PreviewNotAvailable')}
              type={NOTIFICATION_TYPE.error}
            />
          ) : null}

          {errors?.length > 0 ? (
            <div className={styles.section}>
              <Text className={styles.sectionTitle}>
                {t('Epost.ValidationErrorsTitleKey')}
              </Text>

              {errors.map(error => (
                <Text
                  key={error[DocFlowEpostValidationPreviewErrorFieldName.Code]}
                >
                  {error[DocFlowEpostValidationPreviewErrorFieldName.Text]}
                </Text>
              ))}
            </div>
          ) : null}

          {previewImage ? (
            <DocFlowEpostValidationPreviewImage
              fileName={previewFileName}
              src={previewImage}
            />
          ) : null}
        </Content>

        <Buttons>
          <Button.Contained
            qaName={QA_NAME_CONFIRM_BTN}
            text={t('Modal.ConfirmBtnKey')}
            onClick={onClose}
          />
        </Buttons>
      </Box>
    </Modal>
  )
}

export default DocFlowEpostValidationPreviewModal
